import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, X, Minimize2, MessageCircle, PanelRight, ChevronDown } from "lucide-react";
import type { WidgetMode } from "@/contexts/AIAssistantContext";
import { ConversationHistory } from "./ConversationHistory";

interface ChatWidgetHeaderProps {
  sessionTitle: string;
  showHistory: boolean;
  onShowHistoryChange: (open: boolean) => void;
  onNewSession: () => void;
  onClose: () => void;
  isNewConversation: boolean;
  isMobile?: boolean;
  widgetMode?: WidgetMode;
  onSetWidgetMode?: (mode: WidgetMode) => void;
}

export function ChatWidgetHeader({
  sessionTitle,
  showHistory,
  onShowHistoryChange,
  onNewSession,
  onClose,
  isNewConversation,
  isMobile = false,
  widgetMode,
  onSetWidgetMode,
}: ChatWidgetHeaderProps) {
  const { t } = useTranslation();

  const showViewMode = widgetMode !== undefined && onSetWidgetMode !== undefined;
  
  return (
    <div className="flex items-center justify-between p-2.5 md:p-3 border-b border-border bg-card flex-shrink-0">
      <Popover open={showHistory} onOpenChange={onShowHistoryChange}>
        <PopoverTrigger asChild>
          <button
            className="flex items-center min-w-0 flex-1 mr-2 hover:opacity-70 transition-opacity text-left"
          >
            <h3 className="font-semibold text-sm truncate">{sessionTitle}</h3>
            <ChevronDown className="h-3 w-3 opacity-50 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          className="w-72 p-0"
        >
          <ConversationHistory onClose={() => onShowHistoryChange(false)} />
        </PopoverContent>
      </Popover>

      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onNewSession}
          disabled={isNewConversation}
          title={t("pages.aiAssistant.newConversation", "New conversation")}
        >
          <Plus className="h-4 w-4" />
        </Button>

        {showViewMode && !isMobile && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hidden md:flex"
                title={t("pages.aiAssistant.viewMode", "View mode")}
              >
                {widgetMode === "floating" ? (
                  <MessageCircle className="h-4 w-4" />
                ) : (
                  <PanelRight className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuRadioGroup
                value={widgetMode}
                onValueChange={(v) => onSetWidgetMode!(v as WidgetMode)}
              >
                <DropdownMenuRadioItem value="floating" className="gap-2">
                  <MessageCircle className="h-4 w-4" />
                  {t("pages.aiAssistant.floating", "Floating")}
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="sidebar" className="gap-2">
                  <PanelRight className="h-4 w-4" />
                  {t("pages.aiAssistant.sidebar", "Sidebar")}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClose}
          title={isMobile ? t("common.actions.close", "Close") : t("pages.aiAssistant.minimize", "Minimize")}
        >
          {isMobile ? (
            <X className="h-4 w-4" />
          ) : (
            <Minimize2 className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
